import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { contractTimelineAPI } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import {
  ArrowRight, ListChecks, CheckCircle2, ClipboardEdit, Activity, MessageSquareText, RotateCcw, Mail, StickyNote,
} from 'lucide-react';

const EVENT_META = {
  status_change:        { icon: ArrowRight,    color: 'bg-sky-100 text-sky-700' },
  followup_created:     { icon: ListChecks,    color: 'bg-amber-100 text-amber-700' },
  followup_completed:   { icon: CheckCircle2,  color: 'bg-emerald-100 text-emerald-700' },
  crm_note_created:     { icon: MessageSquareText, color: 'bg-violet-100 text-violet-700' },
  contract_note:        { icon: StickyNote,    color: 'bg-blue-100 text-blue-800' },
  contract_note_email:  { icon: Mail,          color: 'bg-blue-100 text-blue-800' },
  audit:                { icon: ClipboardEdit, color: 'bg-slate-100 text-slate-700' },
};

export function ContractTimeline({ requestId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!requestId) return;
    setLoading(true);
    contractTimelineAPI.timeline(requestId)
      .then(res => setEvents(res.data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [requestId]);

  return (
    <Card data-testid="contract-timeline">
      <CardHeader>
        <CardTitle className="text-lg font-heading flex items-center gap-2">
          <Activity size={18} className="text-slate-500" /> Contract Timeline
          {events.length > 0 && <span className="text-xs font-normal text-slate-400">({events.length} events)</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-8">Loading…</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">No events yet.</p>
        ) : (
          <ol className="relative border-l-2 border-slate-200 ml-3 space-y-5">
            {events.map((e, i) => {
              // 27.53.G — Distinguish email vs manual contract notes
              // by icon while keeping the same colour theme.
              const metaKey = e.event_type === 'contract_note' && e.source === 'email'
                ? 'contract_note_email'
                : e.event_type;
              const meta = EVENT_META[metaKey] || EVENT_META.audit;
              const Icon = meta.icon;
              return (
                <li key={i} className="ml-6">
                  <span className={`absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full ${meta.color} ring-4 ring-white`}>
                    <Icon size={12} />
                  </span>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-0.5">
                        <p className="font-medium text-slate-900 text-sm">{e.title}</p>
                        {e.activity_type && (
                          <Badge variant="outline" className="text-[10px]">{e.activity_type.replace(/_/g, ' ')}</Badge>
                        )}
                        {e.status && (
                          <Badge variant="outline" className="text-[10px]">{e.status.replace(/_/g, ' ')}</Badge>
                        )}
                        {e.event_type === 'contract_note' && e.source === 'email' && (
                          <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">Email</Badge>
                        )}
                        {e.event_type === 'contract_note' && e.visibility === 'internal' && (
                          <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-900 border-amber-300">Internal</Badge>
                        )}
                        {e.from_predecessor && (
                          <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500 border-slate-300 flex items-center gap-1">
                            <RotateCcw size={9} /> earlier record
                          </Badge>
                        )}
                      </div>
                      {e.event_type === 'contract_note' && (e.from_email || e.subject) && (
                        <p className="text-[11px] text-slate-500">
                          {e.from_email && <span>From: {e.from_email}</span>}
                          {e.from_email && e.subject && <span> · </span>}
                          {e.subject && <span>Subject: {e.subject}</span>}
                        </p>
                      )}
                      {e.description && <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{e.description}</p>}
                      {e.carrier_name && (
                        <p className="text-[11px] text-slate-400 mt-1">Carrier: {e.carrier_name}</p>
                      )}
                      <p className="text-[11px] text-slate-400 mt-1">
                        {e.timestamp ? formatDateTime(e.timestamp) : '—'}
                        {e.actor_email && <span> • {e.actor_email}</span>}
                        {e.due_date && <span> • due {e.due_date}</span>}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

export default ContractTimeline;
